import { expectAtIndex } from "@aprao/common/src/array/expectAtIndex.js";
import { inferReadonlyArray } from "@aprao/common/src/array/inferReadonlyArray.js";
import { pull } from "@aprao/common/src/array/pull.js";
import { assertUnreachable } from "@aprao/common/src/assertUnreachable.js";
import {
  type BigFraction,
  bigFractionFromNumber,
} from "@aprao/common/src/math/BigFraction.js";
import { divide } from "@aprao/common/src/math/divide.js";
import { FRACTION_ZERO } from "@aprao/common/src/math/fraction.js";
import { opposite, oppositeNumber } from "@aprao/common/src/math/opposite.js";
import {
  sum,
  sumKey,
  sumKeyNumbers,
  sumNumbers,
} from "@aprao/common/src/math/sum.js";
import { wrapFraction } from "@aprao/common/src/math/wrapFraction.js";
import type {
  Fraction,
  FundingType,
  RichTextData,
  WithFundingType,
  YearMonth,
} from "@aprao/common/src/shapes/common.js";
import { times } from "@aprao/common/src/util/times.js";
import { makeAutoObservable, toJS } from "mobx";
import { computedFn } from "mobx-utils";
import { nanoid } from "nanoid";

import { createTimelineOverride } from "../createTimelineOverride.js";
import type {
  CashflowOptimizationStrategy,
  CashflowPeriodGranularity,
  ExperimentalFlowKind,
  ReturnTargetDenominator,
  ShapeAppraisalDocument,
  ShapeBuildStage,
  ShapeLendingStage,
  ShapeOtherCostsStage,
  ShapePurchaseStage,
  ShapeRevenueStage,
  ShapeStages,
} from "../shapes/ShapeAppraisal.js";
import type { ShapeBuildPhase } from "../shapes/ShapeBuildPhase.js";
import type { ShapeEscalatedAmount } from "../shapes/ShapeEscalatedAmount.js";
import type {
  ShapeInterestLender,
  ShapeProfitShareLender,
} from "../shapes/ShapeLender.js";
import type { ShapeOtherCostsGroup } from "../shapes/ShapeOtherCostsGroup.js";
import type {
  ShapeOperatedAssetGroup,
  ShapeOtherIncomeGroup,
  ShapeResidentialSalesGroup,
} from "../shapes/ShapeRevenueGroup.js";
import {
  otherIncomeGroupPredicate,
  salesGroupPredicate,
} from "../shapes/ShapeRevenueGroup.js";
import type { ShapeSitePurchase } from "../shapes/ShapeSitePurchase.js";
import { BuildCostsPreliminaryFacade } from "./build/BuildCostsPreliminaryFacade.js";
import {
  BuildPhaseFacade,
  type BuildPhaseLinksBridge,
  createPhaseCost,
} from "./build/BuildPhaseFacade.js";
import type { CashflowFacade } from "./CashflowFacade.js";
import {
  createEscalatedAmount,
  EscalatedAmountFacade,
} from "./EscalatedAmountFacade.js";
import type { AnyLenderFacade } from "./lender/AnyLenderFacade.js";
import { createLenderFee } from "./lender/LenderBaseFacade.js";
import { OtherCostsGroupFacade } from "./OtherCostsGroupFacade.js";
import type { AnyRevenueGroupFacade } from "./revenue/AnyRevenueGroupFacade.js";
import { OperatedAssetGroupFacade } from "./revenue/OperatedAssetGroupFacade.js";
import { OtherIncomeGroupFacade } from "./revenue/OtherIncomeGroupFacade.js";
import { ResidentialSalesGroupFacade } from "./revenue/ResidentialSalesGroupFacade.js";
import { SitePurchaseFacade } from "./SitePurchaseFacade.js";
import type { TimelineFacade } from "./timeline/TimelineFacade.js";

const createBuildPhase = (): ShapeBuildPhase => ({
  refId: nanoid(6),
  name: "",
  fundingType: "debtFunded",
  grossArea: {
    relatives: { absolute: null, ratioNetArea: "120/100" as Fraction },
  },
  definedNetArea: null,
  definedSalesItemsQuantity: null,
  costs: times(2, (idx) => createPhaseCost(idx + 1)),
  contingency: {
    relatives: { absolute: null, ratioGrossCost: "10/100" as Fraction },
  },
  projectionPeriods: [],
  timelineOverride: createTimelineOverride(),
});

const createSitePurchase = (): ShapeSitePurchase => ({
  description: "",
  landTax: {
    useClassCurrent: "Residential.2025-04-01",
    calculationType: "auto",
    definedAmount: { relatives: { absolute: null, ratioPrice: null } },
    surcharge: "5/100" as Fraction,
    projectionPeriods: [],
    timelineOverride: createTimelineOverride(),
  },
  pricePurchase: {
    calculationType: "manual",
    definedAmount: null,
    projectionPeriods: [],
    timelineOverride: createTimelineOverride(),
  },
  customCosts: [
    {
      name: "Agent fee",
      relatives: {
        absolute: null,
        ratioPrice: "15/1000" as Fraction,
      },
      projectionPeriods: [],
      timelineOverride: createTimelineOverride(),
    },
    {
      name: "Legal fee",
      relatives: {
        absolute: null,
        ratioPrice: "5/1000" as Fraction,
      },
      projectionPeriods: [],
      timelineOverride: createTimelineOverride(),
    },
  ],
});

const createOtherCostGroup = (): ShapeOtherCostsGroup => ({
  name: "",
  costs: [],
});

const createResidentialSalesGroup = (): ShapeResidentialSalesGroup => ({
  name: "",
  fundingType: "debtFunded",
  category: "residential",
  items: [],
  agentFee: {
    relatives: {
      absolute: null,
      ratioValue: "15/1000" as Fraction,
    },
  },
  legalFee: {
    relatives: {
      absolute: null,
      ratioValue: "5/1000" as Fraction,
    },
  },
  costs: [],
  deposit: {
    relatives: { absolute: null, ratioValueMean: "10/100" as Fraction },
  },
  depositProjectionPeriods: [],
  balanceProjectionPeriods: [],
  depositTimelineOverride: createTimelineOverride(),
  balanceTimelineOverride: createTimelineOverride(),
  linkedBuildPhaseRefId: null,
});

const createOperatedAssetGroup = (): ShapeOperatedAssetGroup => ({
  name: "",
  fundingType: "debtFunded",
  category: "operated",
  periodGranularity: "monthly",
  items: [],
  operatingCosts: [],
  yield: null,
  rentFreeMonthsCount: null,
  costs: [],
  projectionPeriods: [],
  timelineOverride: createTimelineOverride(),
  linkedBuildPhaseRefId: null,
});

const createOtherIncomeGroup = (): ShapeOtherIncomeGroup => ({
  category: "other",
  name: "",
  shouldIncludeInGdv: false,
  fundingType: "debtFunded",
  items: [],
  projectionPeriods: [],
  timelineOverride: createTimelineOverride(),
});

const createInterestLender = (): ShapeInterestLender => ({
  name: "",
  facilitySize: {
    relatives: {
      absolute: null,
      ratioDebtFundedDevCost: null,
      ratioDebtFundedGdv: null,
    },
  },
  fees: [
    createLenderFee("Commitment fee", "commitment"),
    createLenderFee("Exit fee", "exit"),
  ],
  loanType: "interest",
  definedTermInMonths: null,
  interest: {
    rate: {
      rateMargin: null,
      ratePeriod: "year",
    },
    formulation: "drawnBalance",
    calculationType: "auto",
    definedAmount: null,
    payoutType: "retained",
    definedAllowance: null,
  },
});

const createProfitShareLender = (): ShapeProfitShareLender => ({
  name: "",
  facilitySize: {
    relatives: {
      absolute: null,
      ratioDebtFundedDevCost: null,
      ratioDebtFundedGdv: null,
    },
  },
  fees: [],
  loanType: "profitShare",
  profitShare: null,
});

const makeFundingTypePredicate =
  (filterFundingType: FundingType | "any") =>
  ({ fundingType }: WithFundingType) =>
    filterFundingType === "any" || filterFundingType === fundingType;

export const forwardFundedPredicate = makeFundingTypePredicate("forwardFunded");

/**
 * Filters the revenue group that have been excluded from GDV.
 *
 * These kind of groups also don't incur sales tax.
 */
export const additionalIncomePredicate = (group: AnyRevenueGroupFacade) =>
  "shouldIncludeInGdv" in group && !group.shouldIncludeInGdv;

/**
 * A MobX safe way to switch an item's position with its predecessor.
 */
const rankUp = <TItem>(list: Array<TItem>, item: TItem) => {
  const idx = list.indexOf(item);
  if (0 < idx) {
    const higherIdx = idx - 1;

    const higher = expectAtIndex(list, higherIdx);
    list[higherIdx] = item;
    list[idx] = higher;
  }
};

/**
 * A MobX safe way to switch an item's position with its successor.
 */
const rankDown = <TItem>(list: Array<TItem>, item: TItem) => {
  const idx = list.indexOf(item);
  if (0 <= idx && idx < list.length - 1) {
    const lowerIdx = idx + 1;

    const lower = expectAtIndex(list, lowerIdx);
    list[lowerIdx] = item;
    list[idx] = lower;
  }
};

export class RevenueStageFacade {
  $model;

  constructor(model: ShapeRevenueStage) {
    makeAutoObservable(this);

    this.$model = model;
  }

  _decorateGroup = computedFn(
    (group: AnyRevenueGroupFacade["$model"]): AnyRevenueGroupFacade =>
      group.category === "residential" ? new ResidentialSalesGroupFacade(group)
      : group.category === "operated" ? new OperatedAssetGroupFacade(group)
      : group.category === "other" ? new OtherIncomeGroupFacade(group)
      : assertUnreachable(group),
  );

  get revenueGroups() {
    return this.$model.revenueGroups.map((group) => this._decorateGroup(group));
  }

  //get valueGross() {
  //  let total = FRACTION_ZERO;
  //  for (const group of this.revenueGroups) {
  //    total = total.add(group.valueGross ?? FRACTION_ZERO);
  //  }
  //
  //  return total;
  //}

  getGrossDevelopmentValue = (filterFundingType: FundingType | "any") =>
    sumKey(
      this.revenueGroups
        .filter(makeFundingTypePredicate(filterFundingType))
        .filter((group) => !additionalIncomePredicate(group)),
      "valueGross",
    );
}

export class PurchaseStageFacade {
  $model;

  constructor(model: ShapePurchaseStage) {
    makeAutoObservable(this);

    this.$model = model;
  }

  readonly _decorateSitePurchase = computedFn(
    (site: ShapeSitePurchase) => new SitePurchaseFacade(site),
  );

  get sitePurchases() {
    return this.$model.sitePurchases.map((purchase) =>
      this._decorateSitePurchase(purchase),
    );
  }
}

export class BuildStageFacade {
  $model;

  _decorateBuildPhase;

  constructor(
    model: ShapeBuildStage,
    buildPhaseDecorator: (phase: ShapeBuildPhase) => BuildPhaseFacade,
  ) {
    makeAutoObservable(this);

    this.$model = model;
    this._decorateBuildPhase = computedFn(buildPhaseDecorator);
  }

  get preliminary() {
    return new BuildCostsPreliminaryFacade(this.$model.preliminary);
  }

  get buildPhases() {
    return this.$model.buildPhases.map((phase) =>
      this._decorateBuildPhase(phase),
    );
  }

  get buildCostMainTotal() {
    return sumKey(this.buildPhases, "costTotal");
  }

  get buildCostGlobalTotal() {
    return sum([this.preliminary.totalCost, this.buildCostMainTotal]);
  }
}

export class OtherCostsStageFacade {
  $model;

  _decorateOtherCostGroup;

  constructor(
    model: ShapeOtherCostsStage,
    otherCostGroupDecorator: (
      group: ShapeOtherCostsGroup,
    ) => OtherCostsGroupFacade,
  ) {
    makeAutoObservable(this);

    this.$model = model;
    this._decorateOtherCostGroup = computedFn(otherCostGroupDecorator);
  }

  get otherCostsGroups() {
    return this.$model.otherCostsGroups.map((group) =>
      this._decorateOtherCostGroup(group),
    );
  }
}

export class LendingStageFacade {
  $model;

  constructor(model: ShapeLendingStage) {
    makeAutoObservable(this);

    this.$model = model;
  }
}

class StagesFacade implements BuildPhaseLinksBridge {
  $model;

  constructor(model: ShapeStages) {
    makeAutoObservable(this);

    this.$model = model;
  }

  getRevenueGroupsByBuildPhase = (phase: BuildPhaseFacade) =>
    this.revenue.revenueGroups
      .filter(salesGroupPredicate)
      .filter(
        ({ linkedBuildPhaseRefId }) => linkedBuildPhaseRefId === phase.refId,
      );

  isBuildPhaseLinkedToRevenueGroups = (phase: BuildPhaseFacade) =>
    0 < this.getRevenueGroupsByBuildPhase(phase).length;

  getLinkedMeasurementsByBuildPhase = (phase: BuildPhaseFacade) => {
    const groups = this.getRevenueGroupsByBuildPhase(phase);

    let totalCount = 0;
    let totalArea = FRACTION_ZERO;
    for (const group of groups) {
      totalCount += group.totalCount;
      if ("areaTotal" in group && group.areaTotal != null) {
        totalArea = totalArea.add(group.areaTotal);
      }
    }

    return {
      totalCount,
      totalArea,
    };
  };

  get revenue() {
    return new RevenueStageFacade(this.$model.revenue);
  }

  get purchase() {
    return new PurchaseStageFacade(this.$model.purchase);
  }

  get build() {
    return new BuildStageFacade(
      this.$model.build,
      (phase) => new BuildPhaseFacade(phase, this),
    );
  }

  get otherCosts() {
    return new OtherCostsStageFacade(
      this.$model.otherCosts,
      (group) =>
        new OtherCostsGroupFacade(group, {
          ratioBuildCostGlobalTotal: () => this.build.buildCostGlobalTotal,
          ratioGdv: () => this.revenue.getGrossDevelopmentValue("any"),
        }),
    );
  }

  get lending() {
    return new LendingStageFacade(this.$model.lending);
  }

  // This action needs to be lifted out of the BuildStageFacade since it needs
  // to access the revenue groups as well.
  removeBuildPhase(phase: BuildPhaseFacade) {
    // We need to reset the references to the phase from revenue groups otherwise
    // it won't be possible to linked them anymore.
    for (const group of this.getRevenueGroupsByBuildPhase(phase)) {
      group.$model.linkedBuildPhaseRefId = null;
    }

    pull(this.$model.build.buildPhases, phase.$model);
  }

  //get otherCosts() {
  //  return new OtherCostsStageFacade(this.$model.otherCosts);
  //}

  //get costsTotal() {
  //  return this.build.costsTotal.add(this.otherCosts.costsTotal);
  //}

  //get netProceeds() {
  //  return this.revenue.valueTotal;
  //}

  //get netCurrencyAmount() {
  //  return this.netProceeds.sub(this.costsTotal);
  //}
}

export class AppraisalDocumentFacade {
  readonly $model;

  constructor(model: ShapeAppraisalDocument) {
    makeAutoObservable(this);

    this.$model = model;
  }

  get title() {
    return this.$model.title;
  }

  get summary() {
    return this.$model.summary;
  }

  get returnTarget() {
    return wrapFraction(this.$model.returnTarget);
  }

  get returnTargetDenominator() {
    return this.$model.returnTargetDenominator;
  }

  get yearMonthCashflowStart() {
    return this.$model.yearMonthCashflowStart;
  }

  get cashflowOptimizationStrategy() {
    return this.$model.cashflowOptimizationStrategy;
  }

  get cashflowPeriodGranularity() {
    return this.$model.cashflowPeriodGranularity;
  }

  /**
   * Controls which flow scheduler implementation will be used for financial
   * calculations. This is here for legacy reasons. All new appraisals should
   * be using the timeline, but we want to keep the legacy scheduler for older
   * appraisals.
   *
   * - `cashflow`: use {@link CashflowFacade}
   * - `timeline`: use {@link TimelineFacade}
   */
  get experimental_flowKind() {
    return this.$model.experimental_flowKind;
  }

  get stages() {
    return new StagesFacade(this.$model.stages);
  }

  get sitePurchases() {
    // todo eventually remove this
    return this.stages.purchase.sitePurchases;
  }

  get buildCostsPreliminary() {
    // todo eventually remove this
    return this.stages.build.preliminary;
  }

  get buildCostsPhases() {
    // todo eventually remove this
    return this.stages.build.buildPhases;
  }

  get otherCostsGroups() {
    // todo eventually remove this
    return this.stages.otherCosts.otherCostsGroups;
  }

  readonly _decorateEscalatedAmount = computedFn(
    (cost: ShapeEscalatedAmount) => new EscalatedAmountFacade(cost),
  );

  get escalatedCosts() {
    return inferReadonlyArray(
      this.$model.escalatedCosts.map((cost) =>
        this._decorateEscalatedAmount(cost),
      ),
    );
  }

  get revenueGroups() {
    // todo eventually remove this
    return this.stages.revenue.revenueGroups;
  }

  get escalatedIncomes() {
    return inferReadonlyArray(
      this.$model.escalatedIncomes.map((income) =>
        this._decorateEscalatedAmount(income),
      ),
    );
  }

  getAdditionalIncomeTotal = (filterFundingType: FundingType | "any") =>
    sumKey(
      this.revenueGroups
        .filter(makeFundingTypePredicate(filterFundingType))
        .filter(additionalIncomePredicate),
      "valueGross",
    );

  /**
   * Returns `true` if any revenue group has been excluded from GDV.
   */
  get hasAdditionalIncome() {
    return this.revenueGroups.some(additionalIncomePredicate);
  }

  get salesAreaTotal() {
    return sumKey(this.revenueGroups.filter(salesGroupPredicate), "areaTotal");
  }

  get salesAreaAssignableLeft() {
    return sum([
      sumKey(this.revenueGroups.filter(salesGroupPredicate), "areaTotal"),
      opposite(sumKey(this.buildCostsPhases, "netArea")),
    ]);
  }

  get salesItemsCountAssignableLeft() {
    return sumNumbers([
      sumKeyNumbers(
        this.revenueGroups.filter(salesGroupPredicate),
        "totalCount",
      ),
      oppositeNumber(sumKeyNumbers(this.buildCostsPhases, "salesItemsCount")),
    ]);
  }

  get salesUnitsCountTotal() {
    let total = 0;
    for (const group of this.revenueGroups.filter(salesGroupPredicate)) {
      total += group.totalCount;
    }
    return total;
  }

  get salesGrossValue() {
    return sumKey(this.revenueGroups.filter(salesGroupPredicate), "valueGross");
  }

  get averageSalesValuePerArea() {
    return divide(this.salesGrossValue, this.salesAreaTotal);
  }

  get averageSalesValue() {
    return divide(
      sumKey(this.revenueGroups.filter(salesGroupPredicate), "valueGross"),
      bigFractionFromNumber(this.salesUnitsCountTotal),
    );
  }

  get incomeUnitsCountTotal() {
    let total = 0;
    for (const group of this.revenueGroups.filter(otherIncomeGroupPredicate)) {
      total += group.totalCount;
    }
    return total;
  }

  get incomeGrossValue() {
    return sumKey(
      this.revenueGroups.filter(otherIncomeGroupPredicate),
      "valueGross",
    );
  }

  get averageIncomeValue() {
    return divide(
      sumKey(
        this.revenueGroups.filter(otherIncomeGroupPredicate),
        "valueGross",
      ),
      bigFractionFromNumber(this.incomeUnitsCountTotal),
    );
  }

  get salesCostTotal() {
    return sumKey(this.revenueGroups.filter(salesGroupPredicate), "costsTotal");
  }

  get grossAreaTotal() {
    return sum(
      this.buildCostsPhases.map(({ grossArea }) =>
        grossArea.relativesSet.getRelative("absolute"),
      ),
    );
  }

  /**
   * Get the total of the build costs for preliminary and phases.
   *
   * This can't be filtered for forward funding because this always includes
   * the preliminary costs.
   */
  getBuildCostTotalWithContingency = (
    filterFundingType: "debtFunded" | "any",
  ) =>
    sum([
      this.buildCostsPreliminary.totalCost,
      this.getBuildPhaseCostTotalWithContingency(filterFundingType),
    ]);

  getBuildPhaseCostTotalWithContingency = (
    filterFundingType: FundingType | "any",
  ) =>
    sumKey(
      this.buildCostsPhases.filter(makeFundingTypePredicate(filterFundingType)),
      "costTotalWithContingency",
    );

  get otherCostsTotal() {
    return sumKey(this.otherCostsGroups, "costsTotal");
  }

  get hasForwardFunding() {
    return (
      this.revenueGroups.some(forwardFundedPredicate) ||
      this.buildCostsPhases.some(forwardFundedPredicate)
    );
  }

  set title(title: string) {
    this.$model.title = title;
  }

  set summary(summary: RichTextData) {
    this.$model.summary = summary;
  }

  set returnTarget(returnTarget: BigFraction | null) {
    this.$model.returnTarget = returnTarget?.toFraction() ?? null;
  }

  set returnTargetDenominator(denominator: ReturnTargetDenominator) {
    this.$model.returnTargetDenominator = denominator;
  }

  set yearMonthCashflowStart(date: YearMonth | null) {
    this.$model.yearMonthCashflowStart = date;
  }

  set cashflowOptimizationStrategy(strategy: CashflowOptimizationStrategy) {
    this.$model.cashflowOptimizationStrategy = strategy;
  }

  set cashflowPeriodGranularity(granularity: CashflowPeriodGranularity) {
    this.$model.cashflowPeriodGranularity = granularity;
  }

  set experimental_flowKind(flowKind: ExperimentalFlowKind) {
    this.$model.experimental_flowKind = flowKind;
  }

  addSitePurchase(site = createSitePurchase()) {
    this.$model.stages.purchase.sitePurchases.push(toJS(site));
  }

  removeSitePurchase(site: SitePurchaseFacade) {
    pull(this.$model.stages.purchase.sitePurchases, site.$model);
  }

  rankUpSitePurchase(site: SitePurchaseFacade) {
    rankUp(this.$model.stages.purchase.sitePurchases, site.$model);
  }

  rankDownSitePurchase(site: SitePurchaseFacade) {
    rankDown(this.$model.stages.purchase.sitePurchases, site.$model);
  }

  addBuildCostsPhase(phase?: ShapeBuildPhase) {
    this.$model.stages.build.buildPhases.push(
      phase == null ? createBuildPhase()
        // In case of copy we need to generate a new ref id since it supposed
        // to be uniq.
      : { ...toJS(phase), refId: nanoid(6) },
    );
  }

  rankUpBuildCostsPhase(phase: BuildPhaseFacade) {
    rankUp(this.$model.stages.build.buildPhases, phase.$model);
  }

  rankDownBuildCostsPhase(phase: BuildPhaseFacade) {
    rankDown(this.$model.stages.build.buildPhases, phase.$model);
  }

  addOtherCostsGroup(group = createOtherCostGroup()) {
    this.$model.stages.otherCosts.otherCostsGroups.push(toJS(group));
  }

  removeOtherCostsGroup(group: OtherCostsGroupFacade) {
    pull(this.$model.stages.otherCosts.otherCostsGroups, group.$model);
  }

  rankUpOtherCostsGroup(group: OtherCostsGroupFacade) {
    rankUp(this.$model.stages.otherCosts.otherCostsGroups, group.$model);
  }

  rankDownOtherCostsGroup(group: OtherCostsGroupFacade) {
    rankDown(this.$model.stages.otherCosts.otherCostsGroups, group.$model);
  }

  addEscalatedCost(cost = createEscalatedAmount()) {
    this.$model.escalatedCosts.push(toJS(cost));
  }

  removeEscalatedCost(cost: EscalatedAmountFacade) {
    pull(this.$model.escalatedCosts, cost.$model);
  }

  addResidentialSalesGroup(group = createResidentialSalesGroup()) {
    this.$model.stages.revenue.revenueGroups.push(toJS(group));
  }

  addOperatedAssetGroup(group = createOperatedAssetGroup()) {
    this.$model.stages.revenue.revenueGroups.push(toJS(group));
  }

  addOtherIncomeGroup(group = createOtherIncomeGroup()) {
    this.$model.stages.revenue.revenueGroups.push(toJS(group));
  }

  removeRevenueGroup(group: AnyRevenueGroupFacade) {
    pull(this.$model.stages.revenue.revenueGroups, group.$model);
  }

  rankUpRevenueGroup(group: AnyRevenueGroupFacade) {
    rankUp(this.$model.stages.revenue.revenueGroups, group.$model);
  }

  rankDownRevenueGroup(group: AnyRevenueGroupFacade) {
    rankDown(this.$model.stages.revenue.revenueGroups, group.$model);
  }

  addEscalatedIncome(income = createEscalatedAmount()) {
    this.$model.escalatedIncomes.push(toJS(income));
  }

  removeEscalatedIncome(cost: EscalatedAmountFacade) {
    pull(this.$model.escalatedIncomes, cost.$model);
  }

  addInterestLender(lender = createInterestLender()) {
    this.$model.stages.lending.lenders.push(toJS(lender));
  }

  addProfitShareLender(lender = createProfitShareLender()) {
    this.$model.stages.lending.lenders.push(toJS(lender));
  }

  removeLender(lender: AnyLenderFacade) {
    pull(this.$model.stages.lending.lenders, lender.$model);
  }

  rankUpLender(lender: AnyLenderFacade) {
    rankUp(this.$model.stages.lending.lenders, lender.$model);
  }

  rankDownLender(lender: AnyLenderFacade) {
    rankDown(this.$model.stages.lending.lenders, lender.$model);
  }
}
